import { clsx } from 'clsx';
import { ICON_PATHS, type IconName } from './icons.data';

/**
 * Icon — the kit's only icon primitive.
 *
 * `icons.md` states the rule: "Never use emoji, never the Tabler webfont."
 * Emoji carry their own colour and render differently per platform, so they
 * cannot inherit `currentColor` from the chip, rail item or button around them —
 * which is precisely how the kit colours its icons.
 *
 * Presentation lives here rather than in the data, so all 123 icons are
 * guaranteed to share a grid, a stroke weight and a cap style. Size through the
 * kit's classes (`ic`, `ic-sm`, `ic-lg`); colour by setting `color` on any
 * ancestor.
 */
export function Icon({
  name,
  size = 'md',
  className,
  title,
}: {
  name: IconName;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Give a name only when the icon is the ONLY label. Decorative by default. */
  title?: string;
}) {
  const path = ICON_PATHS[name];
  if (!path) return null;

  return (
    <svg
      className={clsx('ic', size === 'sm' && 'ic-sm', size === 'lg' && 'ic-lg', className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

export type { IconName };
